/**
 * 패널/쿼리 편집 화면의 "미리보기" 용 합성 데이터 생성기.
 * 실제 쿼리 실행이 아니라, draft 의 chartType / xField / yFields 만으로
 * 시각적 형태를 확인할 수 있게 일관성 있는 가짜 데이터를 만든다.
 *
 * - line  : 최근 30분간 1분 간격의 30개 시계열 포인트 (sine+cosine 합성 파형 + 소량 랜덤)
 *           포인트가 충분해야 X축이 적절한 폭으로 펼쳐지고 추세가 시각적으로 드러남.
 * - vbar/hbar : A~E 5개 카테고리 (yFields 별 의사 난수)
 * - yFields 미지정 시 기본 'series_1' 로 대체하여 차트가 비어보이지 않도록 함
 * - 시간축 혼용 호환: 원 key 외에 't' 별칭도 부여 (PanelChart 가 t 키 선호)
 */
export function buildMockData(draft) {
  const yFields = (draft.yFields && draft.yFields.length > 0) ? draft.yFields : ['series_1'];
  const x = draft.xField || 'x';

  if (draft.chartType === 'line') {
    const POINTS = 30;
    const INTERVAL_MS = 60 * 1000;     // 1분 간격
    const now = Date.now();
    return Array.from({ length: POINTS }).map((_, i) => {
      const row = {};
      const iso = new Date(now - (POINTS - 1 - i) * INTERVAL_MS).toISOString();
      row[x] = iso;
      row.t = iso;
      yFields.forEach((f, yi) => {
        // 시리즈 별로 base level 분리 + 위상 차이 → 라인이 겹치지 않고 추세 비교 가능.
        const base = 50 + yi * 35;
        const slow = 22 * Math.sin((i + yi * 5) * 0.18);   // 느린 큰 흔들림
        const fast = 8  * Math.cos((i + yi * 3) * 0.6);    // 빠른 잔물결
        const noise = (Math.random() - 0.5) * 6;
        row[f] = Math.max(0, Math.round(base + slow + fast + noise));
      });
      return row;
    });
  }

  if (draft.chartType === 'job') {
    // 작업 mock — 완료 1개 + 진행 중 2개. 컬럼명은 draft.jobFields 매핑 따름
    // (없으면 default 컬럼명 사용 → PanelChart 가 jobFields 미설정 시 안내 표시).
    const now = Date.now();
    const jf = draft.jobFields || {};
    const cols = {
      name:         jf.name         || 'name',
      startTime:    jf.startTime    || 'start_time',
      endTime:      jf.endTime      || 'end_time',
      targetSize:   jf.targetSize   || 'target_size',
      currentSize:  jf.currentSize  || 'current_size',
      targetCount:  jf.targetCount  || 'target_count',
      currentCount: jf.currentCount || 'current_count',
    };
    const iso = (ms) => new Date(ms).toISOString();
    return [
      // 완료된 작업
      {
        [cols.name]:         'Load TBL_A',
        [cols.startTime]:    iso(now - 12 * 60000),
        [cols.endTime]:      iso(now - 8 * 60000),
        [cols.targetSize]:   1000, [cols.currentSize]:  1000,
        [cols.targetCount]:  50000, [cols.currentCount]: 50000,
      },
      // 진행 중 — 진행률 67%
      {
        [cols.name]:         'Load TBL_B',
        [cols.startTime]:    iso(now - 6 * 60000),
        [cols.endTime]:      null,
        [cols.targetSize]:   2000, [cols.currentSize]:  1340,
        [cols.targetCount]:  100000, [cols.currentCount]: 67000,
      },
      // 진행 중 — 진행률 31%, 건수 정보 없음
      {
        [cols.name]:         'Index Rebuild',
        [cols.startTime]:    iso(now - 3 * 60000),
        [cols.endTime]:      null,
        [cols.targetSize]:   800, [cols.currentSize]:  250,
      },
    ];
  }

  // bar (vbar/hbar) 또는 그 외 타입
  const categories = ['A', 'B', 'C', 'D', 'E'];
  return categories.map((c, i) => {
    const row = {};
    row[x] = c;
    yFields.forEach((f, yi) => {
      row[f] = Math.round(30 + (20 * (i + yi + 1)) % 80 + Math.random() * 10);
    });
    return row;
  });
}
